import { ClueType } from "@/daily/types";

interface IconProps {
  size?: number;
}

// Top-down airliner, nose pointing east. Rotate the wrapper to aim it.
export const PlaneIcon = ({ size = 20 }: IconProps) => (
  <svg width={size} height={size} viewBox="-14 -12 28 24" aria-hidden="true">
    <path
      d="M12 0 L4 -2 L-2 -10 L-5 -10 L-1 -2 L-8 -2 L-11 -6 L-13 -6 L-11 0 L-13 6 L-11 6 L-8 2 L-1 2 L-5 10 L-2 10 L4 2 Z"
      fill="currentColor"
    />
  </svg>
);

const Stroke = ({
  size = 20,
  width = 1.9,
  children,
}: IconProps & { width?: number; children: React.ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={width}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const ClockIcon = ({ size }: IconProps) => (
  <Stroke size={size}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Stroke>
);

export const HelpIcon = ({ size }: IconProps) => (
  <Stroke size={size} width={1.8}>
    <circle cx="12" cy="12" r="10" />
    <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </Stroke>
);

export const CrossIcon = ({ size, width = 2.4 }: IconProps & { width?: number }) => (
  <Stroke size={size} width={width}>
    <path d="M6 6l12 12" />
    <path d="M18 6 6 18" />
  </Stroke>
);

export const CheckIcon = ({ size }: IconProps) => (
  <Stroke size={size} width={2.6}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Stroke>
);

export const ShareIcon = ({ size }: IconProps) => (
  <Stroke size={size} width={2}>
    <path d="M12 15V3" />
    <path d="m7 8 5-5 5 5" />
    <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
  </Stroke>
);

export const ArrowIcon = ({ size }: IconProps) => (
  <Stroke size={size} width={2.2}>
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </Stroke>
);

export const CopyIcon = ({ size }: IconProps) => (
  <Stroke size={size} width={2}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
  </Stroke>
);

const CLUE_PATHS: Record<ClueType, string> = {
  flight_time: "M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18M12 7v5l3 2",
  time_left: "M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18M12 7v5l3 2",
  on_arrival:
    "M12 8a4 4 0 1 0 0 8a4 4 0 0 0 0-8M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4",
  airline:
    "M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z",
  // A map pin.
  possible_airports: "M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21zM12 7a2.5 2.5 0 1 0 0 5a2.5 2.5 0 0 0 0-5",
  local_knowledge:
    "M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9s1.3-6.3 3.8-9",
};

export const ClueIcon = ({ type, size }: IconProps & { type: ClueType }) => (
  <Stroke size={size}>
    <path d={CLUE_PATHS[type]} />
  </Stroke>
);
